import { useState } from 'react';
import { ChevronDown, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { FAQ } from '../../../../shared/types/partnerships';

interface FAQAccordionProps {
  faqs: FAQ[];
  canEdit?: boolean;
  onEdit?: (faq: FAQ) => void;
}

export function FAQAccordion({ faqs, canEdit, onEdit }: FAQAccordionProps) {
  const { t } = useTranslation();
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setOpenIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Group by category
  const grouped = faqs.reduce<Record<string, FAQ[]>>((acc, faq) => {
    const cat = faq.category || t('partnerships.faq.default_category');
    (acc[cat] = acc[cat] || []).push(faq);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {Object.entries(grouped).map(([category, items]) => (
        <div key={category}>
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            {category}
          </h3>
          <div className="space-y-1">
            {items.map(faq => {
              const isOpen = openIds.has(faq.faqId);
              return (
                <div key={faq.faqId} className="group border rounded-lg overflow-hidden">
                  <button
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/50 transition-colors"
                    onClick={() => toggle(faq.faqId)}
                  >
                    <span className="text-sm font-medium pr-4">{faq.question}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      {canEdit && onEdit && (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={e => { e.stopPropagation(); onEdit(faq); }}
                          onKeyDown={e => e.key === 'Enter' && (e.stopPropagation(), onEdit(faq))}
                          className="p-1 rounded hover:bg-muted opacity-0 group-hover:opacity-100"
                        >
                          <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                        </span>
                      )}
                      <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </div>
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 pt-1 border-t bg-muted/20">
                      <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{faq.answer}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
